/** Nederlandse inhoud. De modusnamen volgen de catalogus `nl.json`. */
import type { LanguageContent } from '../registry.js';

export const NL: LanguageContent = {
  home: {
    intro: [
      'Retusch is een online tekenspel voor 2 tot 10 spelers, speelbaar in de browser, zonder installatie en zonder account. Elke ronde krijg je een schilderij waaruit één vakje is gewist: schilder het zo dat het verdwijnt in het werk. Daarna draaien de rollen om, en moet je de retouches van de anderen opsporen.',
      'De meeste tekenspellen belonen degene die het snelst wordt herkend. Retusch vraagt het omgekeerde: winnen is niet gezien worden.',
    ],
    steps: [
      {
        title: 'Een schilderij met een gat',
        text: 'Je krijgt een illustratie waaruit één vakje is gewist.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Jij schildert het bij',
        text: 'Je tekent het vakje opnieuw zodat het opgaat in het werk.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'De jacht',
        text: 'Het schilderij komt terug met ieders retouches: vind ze vóór de anderen.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'De onthulling',
        text: 'Elke schuilplaats wordt onthuld, en de punten vallen.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Gezamenlijke jacht',
        text: 'Iedereen schildert op hetzelfde schilderij, elk in zijn eigen vakje. Daarna opent de jacht voor iedereen tegelijk: een retouche wordt maar één keer gevonden, door wie hem het eerst ziet. Dit is de standaardmodus, die waarin altijd iets gebeurt.',
      },
      {
        name: 'Stream',
        text: 'De gastheer tekent niet: hij zoekt, alleen, terwijl alle anderen zich in zijn schilderij verstoppen. Bedacht voor de livestream, waar de jacht de show wordt. Voor altijd gratis.',
      },
      {
        name: 'Ieder zijn schilderij',
        text: 'Eén werk per speler. Je zoekt alleen de retouche van één iemand, in één klik. De eenvoudigste regel van het spel, en de spannendste.',
      },
      {
        name: 'Eén tekent',
        text: 'Eén ronde, één tekenaar, alle anderen achter hem aan. De rol rouleert, en de tekenaar ziet zijn eigen schuilplaats vallen of standhouden.',
      },
      {
        name: 'Battle royale',
        text: 'Dezelfde jacht, maar de eerste ontdekte retouche schakelt de maker uit. Er wordt gespeeld tot de laatste overblijft, zonder één punt te tellen.',
      },
      {
        name: 'Wedstrijd',
        text: 'Niemand zoekt. Iedereen schildert hetzelfde vakje van hetzelfde schilderij bij, en daarna stemt elk op de retouche die hij het mooist vindt. Eén stem is één punt.',
      },
    ],
    faq: [
      {
        question: 'Hoeveel spelers heb je nodig?',
        answer:
          'Van 2 tot 10. Ben je alleen, dan neemt een automatische tegenstander de vrije plek in zodat het spel meteen begint. Vanaf vier komt de gezamenlijke jacht pas echt tot zijn recht.',
      },
      {
        question: 'Is het gratis?',
        answer:
          'Ja. Het snelle spel, de gezamenlijke jacht en de streammodus zijn gratis, zonder tijdslimiet en zonder maximum aantal partijen. De andere modi zijn dat voorlopig ook.',
      },
      {
        question: 'Heb ik een account nodig?',
        answer:
          'Nee. Bijnaam typen, klikken, spelen. Een account bewaart alleen je bijnaam en je avatar van de ene partij naar de volgende: het blijft optioneel.',
      },
      {
        question: 'Werkt het op een telefoon?',
        answer:
          'Ja, de interface past zich aan aanraking aan: ingeklapte werkbalk, loep onder je vinger, dubbeltik om in te zoomen. Dat gezegd hebbende teken je nauwkeuriger met een muis en een groot scherm.',
      },
      {
        question: 'Hoe nodig ik mijn vrienden uit?',
        answer:
          'Maak een kamer aan, kopieer de uitnodigingslink en plak hem waar je wilt. Je vrienden komen rechtstreeks aan tafel, zonder account en zonder installatie.',
      },
      {
        question: 'Wat is een «retouche»?',
        answer:
          'Dat is wat je in het gewiste vakje schildert. Goed gedaan gaat het door voor een stuk van het oorspronkelijke schilderij en vindt niemand het. Slecht gedaan springt het eruit.',
      },
    ],
    headings: {
      intro: 'Een tekenspel waarin je moet verdwijnen',
      steps: 'Zo speel je',
      modes: 'Zes manieren om te spelen',
      faq: 'Veelgestelde vragen',
      more: 'Verder lezen',
    },
    links: {
      rules: 'Lees de volledige regels en de puntentelling',
      modes: 'Vergelijk de modi in detail',
    },
  },

  howToPlay: {
    title: 'Retusch spelen',
    summary:
      'De volledige regels van Retusch: het verloop van een ronde, de puntentelling aan beide kanten, de tekengereedschappen en tips om je te verstoppen én om te zoeken.',
    sections: [
      {
        heading: 'Het idee in drie zinnen',
        blocks: [
          'Je krijgt een schilderij waaruit één vakje is gewist, en je schildert het zo bij dat het onopgemerkt blijft.',
          'Daarna kijkt iedereen naar de geretoucheerde schilderijen en zoekt de bijgeschilderde vakjes.',
          'Je scoort door snel te vinden, en je scoort ook zolang je eigen schuilplaats standhoudt.',
        ],
      },
      {
        heading: 'Het verloop van een ronde',
        blocks: [
          'Een partij rijgt identieke rondes aaneen, standaard vijf. Elke ronde bestaat uit drie momenten.',
          {
            list: [
              'Het tekenen. Je krijgt je schilderij en je vakje, en standaard 2 minuten om het bij te schilderen. De knop Klaar geldt als inleveren: zodra iedereen heeft ingeleverd gaat de ronde door zonder op de klok te wachten.',
              'De jacht. Het schilderij verschijnt weer met de retouches, en je hebt standaard 30 seconden om ze te vinden. Een klik op een bijgeschilderd vakje toont het aan de hele tafel.',
              'De onthulling. Alle schuilplaatsen krijgen een kader en een naam, ook die niemand heeft gezien. Met een schuifregelaar vergelijk je het geretoucheerde schilderij met het origineel.',
            ],
          },
        ],
      },
      {
        heading: 'De puntentelling',
        blocks: [
          'Twee tellers lopen tegelijk, en ze werken tegen elkaar in: wie zoekt wil snel zijn, wie getekend heeft wil dat zijn vakje standhoudt.',
          {
            list: [
              'Als zoeker: 1000 punten als je binnen de eerste 3 seconden vindt, daarna een gelijkmatige daling tot 100 punten aan het eind van de fase. Missen of niets vinden levert niets op.',
              'Als tekenaar: slechts 100 punten als je vakje meteen valt, daarna een gelijkmatige stijging tot 800 punten als het tot het eind standhoudt. Een retouche die niemand vindt is het maximum waard, 800 punten.',
            ],
          },
          'De tekenaar wordt maar één keer betaald, bij de onthulling, en op basis van de snelste zoeker. Het maakt dus niet uit of twee of acht mensen je zoeken: alleen wie je het eerst vindt telt.',
          'De puntentelling blijft gelijk, welke duur je ook instelt voor de fase. Een langere jacht verschuift de twee grenzen niet, alleen de helling ertussen.',
          'Eén fout per ronde. De eerste klik die geen enkele retouche raakt beëindigt jouw jacht: zonder die regel zou je het beeld willekeurig afstruinen tot je er per ongeluk een raakt. Twee klikken kosten niets: op je eigen vakje, en op een al gevonden retouche.',
        ],
      },
      {
        heading: 'De tekengereedschappen',
        blocks: [
          {
            list: [
              'De kwast (B): drie snelle maten, plus een schuifregelaar voor de exacte maat. De sneltoetsen 1, 2 en 3 kiezen de drie maten.',
              'De verfemmer (P): vult in één keer een egaal vlak, zonder ooit buiten je vakje te lopen.',
              'De pipet (I): pakt een kleur waar dan ook op het schilderij. Alt ingedrukt houden schakelt er één klik naartoe en geeft daarna het vorige gereedschap terug.',
              'De hand (H): verschuift het beeld. Buiten je vakje doen de kwast en de emmer hetzelfde, dus je hoeft bijna nooit van gereedschap te wisselen.',
              'Ongedaan maken (Ctrl+Z of Cmd+Z): gaat streek voor streek terug, tot dertig keer. Opnieuw uitvoeren bestaat niet.',
              'Het achtergrondkleurtje aan het eind van het palet: dat is de startkleur van je vakje. Ermee schilderen komt neer op wissen, alleen is het een streek als elke andere, die je ongedaan maakt en overschildert. Daarom is er geen gum.',
            ],
          },
        ],
      },
      {
        heading: 'Vijf tips om je te verstoppen',
        blocks: [
          {
            list: [
              'Pak kleuren, raad ze niet. De pipet geeft de exacte kleur van de omgeving: een vlak dat er net naast zit valt van ver op, terwijl een exact vlak onzichtbaar wordt.',
              'Zet door wat je vakje doorkruist. Een muur, een horizon, een tak die aan de ene kant binnenkomt moet er aan de andere kant op de juiste plek weer uit. Een onderbroken lijn is de eerste aanwijzing waar een ervaren speler naar zoekt.',
              'Schilder eerst de hoeken. Dit is de meest gemaakte fout: het midden is verzorgd, de vier hoeken blijven de oorspronkelijke achtergrond, en het vierkant valt met het blote oog op zonder dat je de tekening zelfs maar hoeft te bekijken.',
              'Werk van grof naar fijn. Zet eerst de grote kleurvlakken met een dikke kwast, verfijn daarna. Andersom vreet één hoek al je tijd op terwijl de rest leeg blijft.',
              'Neem het detailniveau van de omgeving over. Een te net vakje midden in een druk gebied valt net zo op als een slordig vakje midden in een egaal vlak.',
            ],
          },
        ],
      },
      {
        heading: 'Vier tips om te zoeken',
        blocks: [
          {
            list: [
              'Zoek de lijnen die ophouden. Een rand, een kabel, een tafelrand die een haartje verspringt: daar zit het bijna altijd.',
              'Eerst overzien, dan inzoomen. In het volledige beeld ziet een slecht ingepast vakje eruit als een vlek. Zoom dient om te bevestigen, niet om te zoeken.',
              'Wantrouw het te gladde. Een volmaakt egaal oppervlak midden in een structuur is even verdacht als een veeg.',
              'Klik niet zomaar. Je hebt recht op één fout: drie seconden controleren kost minder dan de hele ronde.',
            ],
          },
        ],
      },
      {
        heading: 'En per modus?',
        blocks: [
          'Al het bovenstaande beschrijft de gezamenlijke jacht, de standaardmodus. De andere modi veranderen wie tekent, wie zoekt en hoe je wint: de details staan op de pagina met spelmodi.',
        ],
      },
    ],
  },

  modes: {
    title: 'De spelmodi van Retusch',
    summary:
      'De zes manieren om Retusch te spelen: gezamenlijke jacht, stream, ieder zijn schilderij, één tekent, battle royale en wedstrijd. Verloop, aanbevolen aantal spelers en duur.',
    sections: [
      {
        heading: 'Gezamenlijke jacht',
        blocks: [
          'De standaardmodus, en degene om mee te beginnen.',
          'Iedereen tekent op hetzelfde schilderij, elk in zijn eigen vakje. Zodra de retouches binnen zijn komt het werk in zijn geheel terug en opent de jacht voor iedereen tegelijk. Een retouche wordt maar één keer gevonden, door wie hem het eerst ziet: hij verschijnt dan voor de hele tafel en is uit het spel. Je eigen retouche vind je nooit, en een schuilplaats die niemand ziet levert de maker het maximum op.',
          'Ideaal voor een grote groep: er is geen dode tijd, iedereen zoekt tegelijk, en elke vondst verschijnt op ieders scherm.',
          'Aanbevolen: 3 tot 10 spelers.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'Het spiegelbeeld van «één tekent», bedacht voor de livestream.',
          'Alle spelers tekenen op hetzelfde schilderij, behalve de gastheer. Daarna is de gastheer de enige die zoekt, terwijl zijn publiek toekijkt hoe hij zwoegt. Hij scoort geen punten en staat niet in het klassement: er wordt gespeeld tussen wie zich verstopt, hij leidt de jacht. Hij mag zich zo vaak vergissen als hij wil, de regel van de ene fout geldt niet voor hem.',
          'Ideaal voor een live-avond, en om een precieze reden: omdat de gastheer niet tekent, ziet het publiek zijn schuilplaats nooit ontstaan. In de andere modi kijken ze toe hoe hij zijn vakje inschildert, en heeft de jacht geen geheim meer.',
          'Aanbevolen: 4 tot 10 spelers. Gratis, en dat blijft zo.',
        ],
      },
      {
        heading: 'Ieder zijn schilderij',
        blocks: [
          'Elke speler krijgt zijn eigen werk en verstopt daarin zijn retouche. Bij de jacht krijg je het schilderij van één andere speler, en heb je één klik om het bijgeschilderde vakje aan te wijzen. Geen race, geen concurrentie: alleen jij, een schilderij en een beslissing.',
          'Ideaal met zijn tweeën, of wanneer je een rustig spel wilt waarin iedereen de tijd neemt.',
          'Aanbevolen: 2 tot 10 spelers.',
        ],
      },
      {
        heading: 'Eén tekent',
        blocks: [
          'Eén ronde, één tekenaar, alle anderen achter hem aan.',
          'Elke ronde krijgt één persoon een schilderij. De anderen wachten en zoeken daarna zijn retouche, elk beoordeeld op zijn eigen tijd. De tekenaar wordt beoordeeld op de snelste zoeker, en ziet zijn bevroren schuilplaats terwijl ernaar gezocht wordt. De rol rouleert: de instelling voor rondes telt hier volledige omlopen, want een partij is alleen eerlijk als iedereen even vaak heeft getekend.',
          'Ideaal voor een kleine groep die zich op gelijke voet wil meten, tekening na tekening.',
          'Aanbevolen: 3 tot 6 spelers, daarboven wordt het wachten lang.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'De gezamenlijke jacht, met één afvaller per ronde.',
          'Hetzelfde gedeelde schilderij, dezelfde gelijktijdige jacht. Maar hier worden geen punten geteld: de enige vraag is wie blijft. De speler wiens retouche het eerst wordt ontdekt verlaat het spel. De anderen blijven zoeken tot het eind van de ronde, wat niemand meer laat afvallen maar ieder zijn plaats geeft, getoond op zijn vakje. Wordt niemand gevonden, dan valt niemand af.',
          'Ideaal om een avond mee af te sluiten: de spanning stijgt terwijl de tafel leegloopt, en de laatste ronde is een duel.',
          'Aanbevolen: 4 tot 10 spelers.',
        ],
      },
      {
        heading: 'Wedstrijd',
        blocks: [
          'De enige modus waarin niemand zoekt.',
          'Iedereen schildert hetzelfde vakje van hetzelfde schilderij bij. Daarna bekijkt elk in zijn eigen tempo de retouches van de anderen en stemt op degene die hij het geslaagdst vindt. Een ontvangen stem levert de maker één punt op. Tijdens het stemmen zijn de makers anoniem, en op jezelf stemmen kan niet: namen en stemmen komen pas bij de onthulling naar buiten.',
          'Ideaal wanneer de groep gemengd is, of wanneer sommigen liever tekenen zonder de druk van de jachtklok.',
          'Aanbevolen: 3 tot 10 spelers.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Gezamenlijke jacht', players: '3 tot 10', duration: '15 tot 20 min' },
    { name: 'Stream', players: '4 tot 10', duration: '15 tot 20 min' },
    { name: 'Ieder zijn schilderij', players: '2 tot 10', duration: '15 tot 20 min' },
    { name: 'Eén tekent', players: '3 tot 6', duration: '20 tot 35 min' },
    { name: 'Battle royale', players: '4 tot 10', duration: '20 tot 30 min' },
    { name: 'Wedstrijd', players: '3 tot 10', duration: '10 tot 15 min' },
  ],
  modeTableHeadings: ['Modus', 'Spelers', 'Duur'],
  premium: {
    heading: 'Gratis modi en betaalde modi',
    blocks: [
      'De gezamenlijke jacht en de streammodus zijn definitief gratis.',
      'De andere vier worden betaald bij het AANMAKEN van een partij, en alleen dan. Meedoen blijft altijd gratis: het volstaat dat één persoon aan tafel het spel heeft vrijgespeeld om iedereen alle modi te laten spelen, zonder account en zonder te betalen.',
      'Vrijspelen wordt een eenmalige aankoop, zonder abonnement, en haalt ook de reclame weg voor het account dat hem heeft gedaan. Tot die tijd staan alle modi voor iedereen open.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Wat is Retusch?',
      date: '2026-08-21',
      summary:
        'Een online tekenspel voor meerdere spelers, qua vorm dicht bij Gartic Phone, maar met een mechaniek die eerder aan Meccha Chameleon doet denken: in plaats van herkend te worden probeer je te verdwijnen.',
      sections: [
        {
          heading: 'Een tekenspel waarin herkend worden verliezen is',
          blocks: [
            'Retusch is een online tekenspel voor 2 tot 10 spelers. Elke ronde krijg je een illustratie waaruit één vakje is gewist, en je moet het zo bijschilderen dat het opgaat in het werk. Daarna kijkt iedereen naar de geretoucheerde schilderijen en zoekt de bijgeschilderde vakjes.',
            'Wat het onderscheidt past in één zin: winnen is niet gezien worden.',
          ],
        },
        {
          heading: 'Wat het deelt met Gartic Phone',
          blocks: [
            'De vorm is dezelfde: je opent een link, typt een bijnaam en speelt. Geen account, geen installatie, geen client om te downloaden. Het spel draait in de browser en een partij begint binnen seconden. Vrienden nodig je uit door een link in een gesprek te plakken, en de tafel loopt vol.',
            'Ook het ritme is vertrouwd: korte rondes, een klok, een tekenfase en dan een fase waarin je ontdekt wat de anderen hebben gemaakt. En vooral die gezamenlijke onthulling aan het eind van de ronde waarin iedereen commentaar geeft op wat hij ziet. Het is hetzelfde groepsplezier.',
            'Net als bij Gartic Phone hoef je niet te kunnen tekenen. Een goed geplaatst stukje lucht verslaat een meesterwerk dat buiten de lijnen loopt.',
          ],
        },
        {
          heading: 'Wat het ontleent aan Meccha Chameleon',
          blocks: [
            'In de kern ligt de mechaniek echter elders. In Meccha Chameleon, een driedimensionaal spel op Steam, beschilder je je eigen personage en zet je het vervolgens in een decor, in de hoop dat niemand het van zijn omgeving kan onderscheiden. De winst komt van de gelijkenis en de goede plaatsing, niet van de actie.',
            'Retusch neemt dat idee over en brengt het terug naar het beeld. Je kiest niet waar je je verstopt, het spel geeft je het vakje; daar staat tegenover dat je volledig maakt wat erin komt. De schuilplaats slaagt als de blik eroverheen glijdt zonder te blijven hangen.',
            'En zoals in elk camouflagespel komt de spanning van de dubbele rol. Je bent tegelijk verstopt en jager: terwijl jij het schilderij afspeurt op een verdacht vlak, speurt iemand het jouwe af.',
          ],
        },
        {
          heading: 'Wat nergens anders bestaat',
          blocks: [
            'De puntentelling beloont beide kampen tegengesteld. Wie zoekt scoort meer naarmate hij sneller vindt; wie getekend heeft scoort meer naarmate zijn vakje langer standhoudt. Een retouche die niemand ziet levert de maker het maximum op.',
            'Daaruit ontstaat een heel eigen speelwijze: het gaat er niet om iets moois te maken, maar iets kloppends. De juiste kleur pakken, een lijn op de juiste plek doortrekken, geen hoek leeg laten. Dat zijn handelingen van kijken voordat het handelingen van tekenen zijn.',
            'Om het in de praktijk te begrijpen kun je het beste de volledige regels lezen, of een snelle partij starten: die begint zonder instellingen en zonder wachten.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Hoe word je onverslaanbaar in Retusch?',
      date: '2026-08-21',
      summary:
        'De sneltoetsen, de aanpak van grof naar fijn en de fout van de lege hoeken: drie dingen die een beginner scheiden van een speler die niemand meer vindt.',
      sections: [
        {
          heading: 'Leer de sneltoetsen en je verdubbelt je bruikbare tijd',
          blocks: [
            'Twee minuten tekenen gaan erg snel voorbij als je elk gereedschap met de muis moet halen. Sneltoetsen winnen makkelijk twintig of dertig seconden per ronde terug, en juist die seconden besteed je aan de details.',
            {
              list: [
                'B voor de kwast, P voor de verfemmer, I voor de pipet, H voor de hand.',
                '1, 2 en 3 voor de drie kwastmaten, zonder langs de werkbalk te gaan.',
                'Ctrl+Z, of Cmd+Z op een Mac, om streek voor streek ongedaan te maken.',
                'Alt ingedrukt houden schakelt naar de pipet en geeft het vorige gereedschap terug zodra je loslaat. Dat is de nuttigste van allemaal: je pakt een kleur midden in de actie zonder ooit van gereedschap te wisselen.',
              ],
            },
            'Een opmerking over AZERTY-toetsenborden: de toetsen 1, 2 en 3 werken zonder Shift, ook al staat er &, é en " op. Het spel leest de positie van de toets, niet het teken dat hij produceert.',
          ],
        },
        {
          heading: 'Schilder van grof naar fijn',
          blocks: [
            'De klassieke beginnersfout is met een hoek beginnen en die mooi maken. De klok loopt af, driekwart van het vakje is nog achtergrond, en de retouche is van tien meter afstand te zien.',
            'De juiste methode is omgekeerd. Begin met een dikke kwast, of met de verfemmer, en zet de grote kleurvlakken: de lucht, de muur, de schaduw op de grond. Na twintig seconden heeft je vakje al de juiste algemene indruk en leest het niet meer als een vreemd blok.',
            'Verklein pas daarna en behandel de overgangen: de horizonlijn die erdoorheen loopt, de rand van een voorwerp, de tint die halverwege verandert. Kom je tijd tekort, dan heb je detail verloren, niet de ronde.',
            'Pak je kleuren altijd met de pipet in plaats van ze uit het palet te kiezen. Een benaderende tint volstaat om een heel vakje te verraden, terwijl de exacte tint het onzichtbaar maakt.',
          ],
        },
        {
          heading: 'Laat de hoeken nooit leeg',
          blocks: [
            'Dit is aanwijzing nummer één, en het eerste waar ervaren spelers naar kijken. Een vakje met een goed behandeld midden maar met vier hoeken in de achtergrondkleur tekent een perfecte rechthoek op het scherm. Je hoeft de tekening niet eens te bekijken: de meetkundige vorm springt eruit.',
            'Wen jezelf aan om eerst de rand te doen en dan het midden. Een goed ingepaste omlijsting vergeeft een ruw binnenwerk; andersom nooit.',
            'Ga bewust over de randen heen. Het spel snijdt je streek af op de grens van het vakje, dus je riskeert niets door ruim verder te gaan, en het is de enige manier om zeker te weten dat geen enkele pixel achtergrond in een hoek overleeft.',
          ],
        },
        {
          heading: 'En als jij aan de beurt bent om te zoeken',
          blocks: [
            'Overzie eerst het hele schilderij zonder in te zoomen: een slecht ingepast vakje lijkt een vlek, en zoom dient alleen om te bevestigen. Volg daarna de lange lijnen, die het beeld doorkruisen: een rand die een haartje verspringt is bijna altijd een retouche.',
            'En onthoud dat je per ronde recht hebt op één fout. Drie seconden controleren kost minder dan de hele ronde.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Een tekenspel voor onder vrienden',
      date: '2026-08-21',
      summary:
        'Waarom Retusch goed werkt in een kleine groep, hoe je uitnodigt met een link, en waarom één speler volstaat om de betaalde modi voor de hele tafel vrij te spelen.',
      sections: [
        {
          heading: 'Een spel dat niemand iets vraagt',
          blocks: [
            'Het grootste obstakel van een spelavond op afstand is de installatie. Een account aanmaken, een client downloaden, een versie die niet klopt: de helft van de groep haakt af voordat er gespeeld is.',
            'Retusch draait in de browser. Je vrienden klikken op een link, typen een bijnaam en zitten aan tafel. Er valt niets te installeren, niets aan te maken, en het werkt even goed op een computer als op een telefoon.',
            'Nog iets wat telt in een groep: niemand valt buiten de boot omdat hij niet kan tekenen. Het spel vraagt niet om iets uit te beelden, alleen om een vakje te laten kloppen met wat eromheen zit. Wie slecht tekent maar goed kijkt wint vaak.',
          ],
        },
        {
          heading: 'Uitnodigen, in de praktijk',
          blocks: [
            'Maak een kamer aan vanaf het beginscherm. Vink «openbare kamer» uit als je onder elkaar wilt blijven: de uitnodiging via link wordt dan de enige toegang.',
            'Kopieer de link met de daarvoor bedoelde knop en plak hem in jullie groepschat, jullie spraakkanaal of jullie berichtenapp. Hij bevat de kamercode: je vrienden komen rechtstreeks aan jouw tafel terecht.',
            'Je kunt de partij instellen terwijl iedereen binnendruppelt: de modus, het aantal rondes, de tekentijd, de grootte van het vakje en zelfs de beeldcategorieën. Niets ligt vast tot je begint.',
          ],
        },
        {
          heading: 'Eén betaalt, iedereen speelt',
          blocks: [
            'Twee modi zijn voor altijd gratis: de gezamenlijke jacht, de standaardmodus, en de streammodus.',
            'De andere worden betaald, maar alleen bij het AANMAKEN van een partij. Meedoen blijft gratis, zonder voorwaarden. In de praktijk volstaat het dat één persoon uit de groep het spel heeft vrijgespeeld om de hele tafel van alle modi te laten genieten, zonder account en zonder iets te betalen.',
            'Vrijspelen is een eenmalige aankoop, zonder abonnement.',
          ],
        },
        {
          heading: 'Welke modus voor welke groep',
          blocks: [
            'Met zijn drieën of vieren volstaat de gezamenlijke jacht, en na één ronde snapt iedereen hem. Vanaf zes wordt hij echt goed: de vondsten volgen elkaar op en het scherm staat nooit stil.',
            'Om een avond af te sluiten kun je battle royale proberen: de tafel loopt ronde na ronde leeg en de laatste is een duel. Voor een gemengde groep, waarin sommigen liever rustig tekenen, haalt de wedstrijd alle druk van de jachtklok weg.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Een tekenspel voor je livestream',
      date: '2026-08-21',
      summary:
        'Retusch heeft een modus voor de livestream, waarin de streamer alleen zoekt terwijl zijn publiek zich verstopt. Hoe je hem gebruikt, en welke andere modi werken voor een publiek.',
      sections: [
        {
          heading: 'Een modus die daarvoor gemaakt is',
          blocks: [
            'De streammodus draait de rollen om. Alle spelers tekenen op hetzelfde schilderij, behalve de gastheer, die helemaal niet tekent. Daarna is de gastheer de enige die zoekt, terwijl zijn publiek toekijkt.',
            'Dat is precies de situatie die een livestream vraagt, en om een precieze reden: omdat de gastheer niet tekent bestaat zijn eigen schuilplaats niet, en heeft de chat niets gezien wat hij niet had mogen zien. De schuilplaatsen van de andere spelers kent het publiek daarentegen wel, en het kan hem er drie keer achter elkaar langs zien klikken.',
            'Voor dit geval zijn twee regels geschreven. De streamer scoort geen punten en staat niet in het klassement: er wordt gespeeld tussen wie zich verstopt, hij leidt de show. En hij mag zich zo vaak vergissen als hij wil, terwijl alle andere modi bij de eerste fout stoppen. Een livestream kan niet eindigen op één ongelukkige klik.',
            'De streammodus is gratis, en dat blijft zo.',
          ],
        },
        {
          heading: 'De wedstrijd, het tweede goede idee',
          blocks: [
            'In de wedstrijdmodus schildert iedereen hetzelfde vakje van hetzelfde schilderij bij, en stemt daarna op de retouche die hij het mooist vindt. Er valt niets meer te vinden, alleen te beoordelen.',
            'Voor een publiek werkt dat uitstekend, en om dezelfde reden als de streammodus: er valt niets te vinden, dus is er niets wat de chat kan verklappen. De inzendingen staan naast elkaar, de stemming levert een moment spanning op, en het onthullen van de namen geeft een nette afsluiting. Het is bovendien de kortste modus.',
          ],
        },
        {
          heading: 'En de andere modi',
          blocks: [
            'Niets verplicht je tot een speciale modus, maar dan speel je beter met vrienden dan met de chat. In alle andere modi schildert de streamer zijn eigen vakje op het scherm: het publiek ziet het ontstaan en kent het antwoord nog voor de jacht begint.',
            'Een partij onder vrienden laat zich daarentegen prima van buitenaf volgen: de tekenfase laat zien hoe ieder zijn schuilplaats bouwt, en de onthulling toont alle vakjes in één keer, met de naam van de maker.',
          ],
        },
        {
          heading: 'De chat in het spel betrekken',
          blocks: [
            'De uitnodigingslink is het enige wat je hoeft te delen. Plak hem in de chat en wie het eerst komt vult de tafel: geen account aanmaken, geen download, geen wachtrij.',
            'Het maximum aantal spelers stel je in de kamer in, tussen 2 en 10. Bij een groot publiek open je beter meerdere korte partijen dan één lange: een ronde duurt minder dan drie minuten.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Wat is er nieuw in Retusch',
      date: '2026-08-21',
      summary:
        'De wijzigingen aan het spel, van de nieuwste naar de oudste: nieuwe modi, nieuwe regels, verbeteringen aan het tekenen en aan de jacht.',
      sections: [
        {
          heading: 'Augustus 2026',
          blocks: [
            {
              list: [
                'Het bij te schilderen vakje begint niet meer als een wit vierkant, maar in de overheersende kleur van wat het bedekt. Je verliest niet langer de helft van je tijd aan het overschilderen van een achtergrond die niet in het schilderij voorkomt.',
                'De verfemmer laat geen lichte rand meer achter langs de streken: hij schuift nu onder hun verzachte rand.',
                'Battle royale telt geen punten meer. De eerst ontdekte speler verlaat het spel, en ieder krijgt een rondeplaats te zien op zijn vakje, van rood tot groen.',
                'De streammodus geeft de streamer zoveel pogingen als hij wil: zijn eerste misser sluit de ronde niet meer af.',
                'In de toeschouwersmodus kun je nu schuiven en inzoomen op het schilderij dat je ziet ontstaan.',
                'Emoji-reacties vertrekken vanaf de bijnaam van de afzender in de spelerslijst, in plaats van vanaf de onderkant van het scherm omhoog te komen.',
                'Buiten het bij te schilderen vakje verschuiven de kwast en de emmer het beeld: je hoeft niet meer naar het handgereedschap te wisselen om je te verplaatsen.',
                'De naam van het spel, linksboven tijdens een partij, brengt je na bevestiging terug naar het beginscherm.',
                'Japans en Koreaans komen bij de vijftien reeds beschikbare talen.',
              ],
            },
          ],
        },
        {
          heading: 'Juli 2026',
          blocks: [
            {
              list: [
                'Nieuwe wedstrijdmodus: iedereen schildert hetzelfde vakje bij, en daarna stemt elk op de retouche die hij het mooist vindt.',
                'Nieuwe streammodus, bedacht voor de livestream: de gastheer zoekt alleen terwijl zijn publiek zich verstopt.',
                'Beeldcategorieën vervangen de moeilijkheidsgraden. Je vinkt aan wat je wilt zien, en elke categorie toont een voorbeeldschilderij.',
                'Twee rondes achter elkaar vallen niet meer op hetzelfde schilderij, en de aangevinkte categorieën wisselen elkaar af van ronde tot ronde.',
                'In de galerij aan het eind van de partij kun je een retouche streek voor streek terugkijken en hem opslaan als afbeelding of als bewegende GIF.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'In één oogopslag',
    allArticles: 'Alle artikelen',
    theRules: 'De spelregels',
    theModes: 'De spelmodi',
  },
};
